import { MouseEvent, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import {
  calculateContextMenuPosition, // closeContextMenuEvent,
  openContextMenuEvent,
} from '@nx-frontend/shared/ui/context-menu';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  BorderPosition,
  PortalBorderType,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
} from '@nx-frontend/shared/types';
import {
  ContextMenuItemData,
  ContextMenuItemType,
  UiContextMenuConfigPosition,
  UiContextMenuTheme,
} from '@nx-frontend/shared/types';
// import { closeAiModalEvent, openAiModalEvent } from './ai-modal';
import { trackInfoMenuOpen } from './analytics';
import { $aiThreads, $assistantFeedback, giveAssistantFeedbackEvent } from './store';
import { getInspectThreadUrl } from './util';

interface ChatbotHeaderProps {
  title: string;
  onBack?: () => void;
  onClose?: () => void;
  isFullscreen?: boolean;
  onFullscreenToggle?: () => void;
  onHistoryClick?: () => void;
  onNewChatClick?: () => void;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onUpdateWorkspaceIndexing: (value: boolean) => void;
  debugThreadId?: string;
  showInfoMenu?: boolean;
  fullScreenButton?: boolean;
  historyButton?: boolean;
  newChatButton?: boolean;
  isPortal?: boolean;
  hideBorder?: boolean;
  cursorMove?: boolean;
}

export function ChatbotHeader({
  title,
  onBack,
  onClose,
  onFullscreenToggle,
  onHistoryClick,
  onNewChatClick,
  themeColors,
  themeStyle,
  onUpdateWorkspaceIndexing,
  debugThreadId,
  showInfoMenu = true,
  fullScreenButton = false,
  historyButton = false,
  newChatButton,
  isFullscreen = false,
  isPortal = false,
  hideBorder = false,
  cursorMove = true,
}: ChatbotHeaderProps) {
  const [
    aiThreads,
    openContextMenu,
    // openAiModal,
    // closeAiModal,
    // closeContextMenu,
    assistantFeedback,
    giveAssistantFeedback,
  ] = useUnit([
    $aiThreads,
    openContextMenuEvent,
    // openAiModalEvent,
    // closeAiModalEvent,
    // closeContextMenuEvent,
    $assistantFeedback,
    giveAssistantFeedbackEvent,
  ]);

  const onInspectThread = useCallback(() => {
    if (debugThreadId) {
      window.open(getInspectThreadUrl(debugThreadId), '_blank');
    }
  }, [debugThreadId]);

  const t = useTranslations('features');

  // const onAgreeIndexingChange = useCallback(
  //   (value: boolean) => {
  //     if (!value) {
  //       closeContextMenu();
  //       openAiModal({
  //         title: t('ai-helper.please_confirm_your_action'),
  //         content: <div className="max-w-[480px]">{t('ai-helper.proceed_deactivate_indexing')}</div>,
  //         buttons: [
  //           {
  //             text: t('ai-helper.cancel'),
  //             size: 'md',
  //             classes: ['!px-6'],
  //             onClick: () => {
  //               closeAiModal();
  //             },
  //           },
  //           {
  //             text: t('ai-helper.ok'),
  //             main: true,
  //             size: 'md',
  //             classes: ['!px-6'],
  //             onClick: () => {
  //               onUpdateWorkspaceIndexing(value);
  //               closeAiModal();
  //             },
  //           },
  //         ],
  //       });
  //     } else {
  //       onUpdateWorkspaceIndexing(value);
  //     }
  //   },
  //   [closeAiModal, closeContextMenu, onUpdateWorkspaceIndexing, openAiModal, t]
  // );

  const onInfoClick = useCallback(
    (event: MouseEvent) => {
      trackInfoMenuOpen();

      const menuItems: ContextMenuItemData[] = [];
      if (aiThreads.usage) {
        menuItems.push({
          type: ContextMenuItemType.HTML,
          html: (
            <>
              <div className="absolute top-0 left-0 w-full h-full bg-white cursor-default z-0"></div>
              <div className="flex items-center justify-between gap-4 bg-white cursor-default relative z-10 h-[32px]">
                <div className="flex items-center gap-2">
                  <Icon icon={IconsMap.CHART} className="w-4 text-neutral-500" />
                  {t('ai-helper.ai_usage_requests')}
                </div>
                <span>
                  {aiThreads.usage.current}
                  <span className="text-neutral-500">/{aiThreads.usage.max}</span>
                </span>
              </div>
            </>
          ),
          isSeparator: true,
          closeOnClick: false,
        });
      }

      menuItems.push(
        ...([
          {
            type: ContextMenuItemType.DEFAULT,
            title: t('ai-helper.help_center'),
            icon: IconsMap.QUESTION,
            additionalStyles: 'h-[42px]',
            action: () => {
              window.open('https://nimbusweb.me/guides/fusebase-ai/ai-assistant-quick-guide/', '_blank');
            },
          },
          {
            type: ContextMenuItemType.DEFAULT,
            title: t('ai-helper.terms_and_privacy'),
            icon: IconsMap.INFO_SHIELD,
            additionalStyles: 'h-[42px]',
            isSeparator: assistantFeedback.given,
            action: () => {
              window.open(
                'https://nimbusweb.me/privacy/?utm_source=app&utm_medium=info_icon&utm_campaign=terms_privacy',
                '_blank'
              );
            },
          },
          ...(assistantFeedback.given
            ? []
            : [
                {
                  type: ContextMenuItemType.DEFAULT,
                  title: t('ai-helper.send_feedback'),
                  icon: IconsMap.AQUARE_BUBBLE_COMMENT,
                  // isSeparator: true,
                  additionalStyles: 'h-[42px]',
                  action: () => {
                    giveAssistantFeedback();
                  },
                },
              ]),
          // {
          //   type: ContextMenuItemType.HTML,
          //   additionalStyles: 'bg-zinc-50 !absolute w-full h-[47px] rounded-b-lg',
          //   html: (
          //     <>
          //       {/*<div className="absolute top-0 left-0 w-full h-full bg-white cursor-default z-0"></div>*/}
          //       <Checkbox
          //         checked={aiThreads.preferences.searchEnabled}
          //         label={t('ai-helper.agree_to_indexing')}
          //         className="relative z-10 h-[31px]"
          //         onChange={onAgreeIndexingChange}
          //       />
          //     </>
          //   ),
          //   closeOnClick: false,
          // },
        ] as ContextMenuItemData[])
      );

      openContextMenu({
        zIndex: 10002,
        items: menuItems,
        position: calculateContextMenuPosition(event.target, 4),
        additionalStyles: '!min-w-[245px]',
        config: {
          position: UiContextMenuConfigPosition.BOTTOM_LEFT,
          menuPadding: 0,
          theme: UiContextMenuTheme.LIGHT,
        },
      });
    },
    [
      // aiThreads.preferences.searchEnabled,
      aiThreads.usage,
      // onAgreeIndexingChange,
      openContextMenu,
      assistantFeedback.given,
      giveAssistantFeedback,
      t,
    ]
  );

  const borderClassNames = getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE, {
    position: BorderPosition.BOTTOM,
  }).join(' ');
  return (
    <div
      className={classNames('flex py-3.5 px-4 justify-between chat-bot-header ', {
        [borderClassNames]: !hideBorder,
        'cursor-move': cursorMove,
      })}
    >
      <div className="flex items-center">
        {onBack ? (
          <button
            className="w-6 h-6 cursor-pointer flex items-center justify-center text-neutral-500 transition-colors duration-200 hover:bg-neutral-200 rounded"
            onClick={onBack}
          >
            <Icon icon={IconsMap.ARROW_2_LEFT} className="w-4" />
          </button>
        ) : null}
        <h3
          className={classNames('font-medium text-sm', {
            'ml-2': onBack,
          })}
        >
          {title}
        </h3>
      </div>
      <div className={classNames('flex gap-1', getTextClassNames(themeColors, PortalTextType.SURFACE_3))}>
        {showInfoMenu && (
          <button
            className="flex items-center justify-center cursor-pointer w-6 h-6 hover:bg-neutral-200 rounded transition-colors duration-200"
            onClick={onInfoClick}
          >
            <Icon icon={IconsMap.INFO} className="w-5" />
          </button>
        )}
        {debugThreadId && (
          <Button
            type={ButtonType.FLAT}
            size={ButtonSize.SM}
            className="pr-2 pl-2 font-normal"
            onClick={onInspectThread}
          >
            <Icon icon={IconsMap.BUG} className="w-4 h-4 mr-2" />
            Inspect thread
          </Button>
        )}
        {onClose && (
          <button
            className="flex items-center justify-center cursor-pointer w-6 h-6 hover:bg-neutral-200 rounded transition-colors duration-200"
            onClick={onClose}
          >
            <Icon icon={IconsMap.MINUS} className="w-5" />
          </button>
        )}
        {newChatButton && (
          <button
            className="flex items-center justify-center cursor-pointer w-6 h-6 hover:bg-neutral-200 rounded transition-colors duration-200"
            onClick={onNewChatClick}
          >
            {<Icon icon={IconsMap.CHAT_NEW} className="w-5" />}
          </button>
        )}
        {historyButton && (
          <button
            className="flex items-center justify-center cursor-pointer w-6 h-6 hover:bg-neutral-200 rounded transition-colors duration-200"
            onClick={onHistoryClick}
          >
            {<Icon icon={IconsMap.HISTORY_CLOCK} className="w-5" />}
          </button>
        )}
        {fullScreenButton && (
          <button
            className="flex items-center justify-center cursor-pointer w-6 h-6 hover:bg-neutral-200 rounded transition-colors duration-200"
            onClick={onFullscreenToggle}
          >
            {!isFullscreen && <Icon icon={IconsMap.FULLSCREEN_IN} className="w-5" />}
            {isFullscreen && <Icon icon={IconsMap.FULLSCREEN_IN_1} className="w-5" />}
          </button>
        )}
      </div>
    </div>
  );
}
